import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Button from '../../../components/ui/Button';

const SearchFilterBar = ({ onSearch, onFilter, filters }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);
  const [activeFilters, setActiveFilters] = useState({
    category: '',
    dietaryPreference: '',
    maxDistance: '',
    priceRange: '',
    urgency: ''
  });

  const categoryOptions = [
    { value: '', label: 'Todas las categorías' },
    { value: 'bakery', label: 'Panadería' },
    { value: 'restaurant', label: 'Restaurante' },
    { value: 'cafe', label: 'Café' },
    { value: 'grocery', label: 'Supermercado' },
    { value: 'dessert', label: 'Postres' }
  ];

  const dietaryOptions = [
    { value: '', label: 'Todas las dietas' },
    { value: 'vegetarian', label: 'Vegetariano' },
    { value: 'vegan', label: 'Vegano' },
    { value: 'gluten-free', label: 'Sin gluten' },
    { value: 'dairy-free', label: 'Sin lácteos' },
    { value: 'halal', label: 'Halal' }
  ];

  const distanceOptions = [
    { value: '', label: 'Cualquier distancia' },
    { value: '5', label: 'Hasta 5 min' },
    { value: '10', label: 'Hasta 10 min' },
    { value: '15', label: 'Hasta 15 min' },
    { value: '30', label: 'Hasta 30 min' }
  ];

  const priceOptions = [
    { value: '', label: 'Cualquier precio' },
    { value: '0-50', label: 'Hasta $50' },
    { value: '50-100', label: '$50 - $100' },
    { value: '100-200', label: '$100 - $200' },
    { value: '200+', label: 'Más de $200' }
  ];

  const urgencyOptions = [
    { value: '', label: 'Cualquier urgencia' },
    { value: 'high', label: 'Alta urgencia' },
    { value: 'medium', label: 'Media urgencia' },
    { value: 'low', label: 'Baja urgencia' }
  ];

  const handleSearch = (e) => {
    const query = e?.target?.value;
    setSearchQuery(query);
    onSearch(query);
  };

  const handleFilterChange = (filterType, value) => {
    const newFilters = { ...activeFilters, [filterType]: value };
    setActiveFilters(newFilters);
    onFilter(newFilters);
  };

  const clearAllFilters = () => {
    const clearedFilters = {
      category: '',
      dietaryPreference: '',
      maxDistance: '',
      priceRange: '',
      urgency: ''
    };
    setActiveFilters(clearedFilters);
    setSearchQuery('');
    onFilter(clearedFilters);
    onSearch('');
  };

  const getActiveFilterCount = () => {
    return Object.values(activeFilters)?.filter(value => value !== '')?.length;
  };

  return (
    <div className="bg-card border border-border rounded-lg p-4 mb-6">
      {/* Search Bar */}
      <div className="flex items-center space-x-3 mb-4">
        <div className="flex-1">
          <Input
            type="search"
            placeholder="Buscar por negocio, tipo de comida..."
            value={searchQuery}
            onChange={handleSearch}
            className="w-full"
          />
        </div>
        
        <Button
          variant="outline"
          onClick={() => setIsFiltersOpen(!isFiltersOpen)}
          iconName="Filter"
          iconPosition="left"
        >
          Filtros
          {getActiveFilterCount() > 0 && (
            <span className="ml-2 bg-accent text-accent-foreground text-xs px-2 py-0.5 rounded-full">
              {getActiveFilterCount()}
            </span>
          )}
        </Button>

        <Button
          variant="ghost"
          onClick={() => console.log('Open map view')}
          iconName="Map"
        />
      </div>
      {/* Quick Filters */}
      <div className="flex flex-wrap gap-2 mb-4">
        <Button
          variant={activeFilters?.urgency === 'high' ? 'default' : 'outline'}
          size="sm"
          onClick={() => handleFilterChange('urgency', activeFilters?.urgency === 'high' ? '' : 'high')}
          iconName="Zap"
          iconPosition="left"
        >
          Urgente
        </Button>
        
        <Button
          variant={activeFilters?.maxDistance === '10' ? 'default' : 'outline'}
          size="sm"
          onClick={() => handleFilterChange('maxDistance', activeFilters?.maxDistance === '10' ? '' : '10')}
          iconName="MapPin"
          iconPosition="left"
        >
          Cerca (10 min)
        </Button>
        
        <Button
          variant={activeFilters?.dietaryPreference === 'vegetarian' ? 'default' : 'outline'}
          size="sm"
          onClick={() => handleFilterChange('dietaryPreference', activeFilters?.dietaryPreference === 'vegetarian' ? '' : 'vegetarian')}
          iconName="Leaf"
          iconPosition="left"
        >
          Vegetariano
        </Button>
      </div>
      {/* Advanced Filters */}
      {isFiltersOpen && (
        <div className="border-t border-border pt-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <Select
              label="Categoría"
              options={categoryOptions}
              value={activeFilters?.category}
              onChange={(value) => handleFilterChange('category', value)}
            />

            <Select
              label="Preferencia dietética"
              options={dietaryOptions}
              value={activeFilters?.dietaryPreference}
              onChange={(value) => handleFilterChange('dietaryPreference', value)}
            />

            <Select
              label="Distancia máxima"
              options={distanceOptions}
              value={activeFilters?.maxDistance}
              onChange={(value) => handleFilterChange('maxDistance', value)}
            />

            <Select
              label="Rango de precio"
              options={priceOptions}
              value={activeFilters?.priceRange}
              onChange={(value) => handleFilterChange('priceRange', value)}
            />

            <Select
              label="Urgencia"
              options={urgencyOptions}
              value={activeFilters?.urgency}
              onChange={(value) => handleFilterChange('urgency', value)}
            />

            <div className="flex items-end">
              <Button
                variant="ghost"
                onClick={clearAllFilters}
                iconName="X"
                iconPosition="left"
                className="w-full"
              >
                Limpiar filtros
              </Button>
            </div>
          </div>
        </div>
      )}
      {/* Active Filters Display */}
      {getActiveFilterCount() > 0 && (
        <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t border-border">
          <span className="text-sm text-muted-foreground">Filtros activos:</span>
          {Object.entries(activeFilters)?.map(([key, value]) => {
            if (!value) return null;
            
            const getFilterLabel = (filterKey, filterValue) => {
              switch (filterKey) {
                case 'category':
                  return categoryOptions?.find(opt => opt?.value === filterValue)?.label;
                case 'dietaryPreference':
                  return dietaryOptions?.find(opt => opt?.value === filterValue)?.label;
                case 'maxDistance':
                  return `Hasta ${filterValue} min`;
                case 'priceRange':
                  return priceOptions?.find(opt => opt?.value === filterValue)?.label;
                case 'urgency':
                  return urgencyOptions?.find(opt => opt?.value === filterValue)?.label;
                default:
                  return filterValue;
              }
            };

            return (
              <div
                key={key}
                className="flex items-center space-x-1 bg-primary text-primary-foreground px-2 py-1 rounded-full text-xs"
              >
                <span>{getFilterLabel(key, value)}</span>
                <button
                  onClick={() => handleFilterChange(key, '')}
                  className="hover:bg-primary-foreground/20 rounded-full p-0.5"
                >
                  <Icon name="X" size={12} />
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default SearchFilterBar;